import { useState, useEffect, useRef, useMemo, Fragment } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronUp, ChevronsUpDown, Loader2, Search } from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { format, parseISO, isValid } from 'date-fns'
import { useUserRole } from '@/hooks/useUserRole'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useYardiInviteList } from '../hooks/useYardiInviteList'
import {
  YARDI_STATUS_MAPPING,
  PER_PAGE_OPTIONS,
  MAIN_SEARCH_DEBOUNCE_MS,
  YARDI_INVITE_COL_WIDTHS_PCT,
} from '../constants'
import type {
  YardiInviteRow,
  YardiInviteListLocationState,
  LrErrorCellProps,
  SortIconProps,
  SortHeaderProps,
} from '../types'
import { YardiPagination } from '../components/YardiPagination'
import { YardiApplicantDetailPanel } from '../components/YardiApplicantDetailPanel'
import { YardiPropertyNameLink } from '../components/YardiPropertyNameLink'
import { YardiInviteFiltersDrawer } from '../components/YardiInviteFiltersDrawer'
import { YardiToolbarFunnelIcon } from '../components/YardiToolbarFunnelIcon'
import {
  formatLrCheckErrorForDisplay,
  getLrCheckErrorTooltipHtml,
  sanitizeDataTestIdSegment,
} from '../utils/yardiInviteUi'
import { cn } from '@/lib/utils/helpers'
import { ConfirmShellModal } from '@/components/ui/ConfirmShellModal'
import { InfoPopover } from '@/components/ui/InfoPopover'
import {
  TableSearchFilterEmptyGridSlot,
  TableSearchFilterEmptyTableRow,
} from '@/components/ui/TableSearchFilterEmptyState'
import yardiTableStyles from '../styles/yardiListing.module.css'

function formatShortDate(v: string | null | undefined): string {
  if (!v) return ''
  try {
    const d = parseISO(String(v).replace(' ', 'T'))
    if (isValid(d)) return format(d, 'MM/dd/yyyy')
    const d2 = new Date(v)
    if (isValid(d2)) return format(d2, 'MM/dd/yyyy')
  } catch {
    /* ignore */
  }
  return String(v)
}

function LrErrorCell({
  invite,
  isLastRow,
  rowSuffix,
}: LrErrorCellProps) {
  const err = invite.lr_check_error
  if (!err) return null

  const line = formatLrCheckErrorForDisplay(err)
  const tipHtml = getLrCheckErrorTooltipHtml(err)
  const showInfo = Boolean(tipHtml)

  return (
    <div
      className="w-full min-w-0 max-w-full text-sm"
      data-testid={`yardi-invite-list-lr-error-cell-${rowSuffix}`}
    >
      {showInfo ? (
        <div className="flex w-full min-w-0 max-w-full justify-start">
          <div className="inline-flex min-w-0 max-w-full items-center gap-0">
            <span className="min-w-0 max-w-full whitespace-normal break-words pr-0.5 text-slate-800" title={line}>
              {line}
            </span>
            <InfoPopover
              contentHtml={tipHtml}
              isLastRow={isLastRow}
              maxWidth={448}
              testId={`yardi-invite-list-lr-error-tooltip-${rowSuffix}`}
              className="[&_ol]:list-decimal [&_ol]:pl-4 [&_li]:pt-1 [&_a]:text-sky-600 [&_a]:underline"
              contentClassName="yardi-lr-check-error-tooltip"
            >
              <button
                type="button"
                data-testid={`yardi-invite-list-lr-error-details-button-${rowSuffix}`}
                className={cn(
                  'inline-flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0',
                  '-translate-x-px',
                  'text-sm leading-none text-green-600 hover:text-green-700',
                  'focus-visible:outline focus-visible:ring-2 focus-visible:ring-green-600/45 focus-visible:ring-offset-1',
                )}
                aria-label="Show full error details"
              >
                <i className="fa fa-info-circle" style={{ color: 'green' }} aria-hidden />
              </button>
            </InfoPopover>
          </div>
        </div>
      ) : (
        <span className="block whitespace-normal break-words text-slate-800" title={line}>
          {line}
        </span>
      )}
    </div>
  )
}

export function YardiInviteListPage() {
  const location = useLocation()
  const {
    orgId,
    companyId,
    propertyId,
    orgName,
    companyName,
    propertyName,
  } = useUserRole()
  const isMobile = useIsMobile()
  const isListView = !isMobile
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null)
  const [autoExpandApplicationId, setAutoExpandApplicationId] = useState<number | null>(() => {
    const state = location.state as YardiInviteListLocationState | null
    return state?.expandApplicationId ?? null
  })
  const [gotoPageInput, setGotoPageInput] = useState('')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const mainSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const autoExpandSearchAppliedRef = useRef(false)

  const list = useYardiInviteList({
    orgId: orgId != null ? String(orgId) : null,
    companyId: companyId != null ? String(companyId) : null,
    propertyId: propertyId != null ? String(propertyId) : null,
    orgName: orgName != null ? String(orgName) : null,
    companyName: companyName != null ? String(companyName) : null,
    propertyName: propertyName != null ? String(propertyName) : null,
  })

  useEffect(
    () => () => {
      if (mainSearchDebounceRef.current) {
        clearTimeout(mainSearchDebounceRef.current)
        mainSearchDebounceRef.current = null
      }
    },
    [],
  )

  useEffect(() => {
    if (autoExpandApplicationId == null || autoExpandSearchAppliedRef.current) return
    autoExpandSearchAppliedRef.current = true
    list.patchFilterForm({ q: String(autoExpandApplicationId) })
    list.filterSubmit()
  }, [autoExpandApplicationId, list.patchFilterForm, list.filterSubmit])

  useEffect(() => {
    if (autoExpandApplicationId == null || list.processing) return
    const found = list.invites?.some((row) => row.yardi_application_id === autoExpandApplicationId)
    if (!found) return
    setSelectedApplicationId(autoExpandApplicationId)
    setAutoExpandApplicationId(null)
  }, [autoExpandApplicationId, list.invites, list.processing])

  const scheduleMainSearchSubmit = () => {
    if (mainSearchDebounceRef.current) {
      clearTimeout(mainSearchDebounceRef.current)
    }
    mainSearchDebounceRef.current = setTimeout(() => {
      mainSearchDebounceRef.current = null
      list.filterSubmit()
    }, MAIN_SEARCH_DEBOUNCE_MS)
  }

  const commitGotoPage = () => {
    const raw = gotoPageInput.trim()
    if (!raw) {
      setGotoPageInput('')
      return
    }
    const n = Number(raw)
    if (!Number.isFinite(n) || n < 1) {
      setGotoPageInput('')
      return
    }
    list.paginateItem(n)
    setGotoPageInput('')
  }

  const gotoPageValue = gotoPageInput || String(list.pageOptions.current ?? 1)

  const toggleApplicant = (id: number) => {
    setSelectedApplicationId((cur) => (cur === id ? null : id))
  }

  const manualLabel = (inv: YardiInviteRow) => (inv.manual ? 'Yes' : 'NO')

  const isReportRoute = location.pathname.includes('/report')
  const pageTitle = isReportRoute ? 'INVITES REPORT' : 'YARDI INVITES'

  const SortIcon = ({ column }: SortIconProps) => {
    const isActive = list.orderBy === column
    return (
      <span
        aria-hidden="true"
        className={cn(
          yardiTableStyles.sortIcon,
          isActive && yardiTableStyles.sortIconActive,
        )}
      >
        {isActive ? (
          list.sortBy === 'asc' ? '▲' : '▼'
        ) : (
          <ChevronsUpDown className="h-3 w-3" />
        )}
      </span>
    )
  }

  const SortHeader = ({ column, children }: SortHeaderProps) => (
    <button
      type="button"
      className={yardiTableStyles.sortHeaderButton}
      onClick={(event) => {
        event.stopPropagation()
        list.sortColumn(column)
      }}
    >
      <span className={yardiTableStyles.sortHeaderLabel}>{children}</span>
      <SortIcon column={column} />
    </button>
  )

  const activeFilterCount = useMemo(() => {
    let c = 0
    if (list.filterForm.q?.trim()) c += 1
    if (!list.readOnlyControl.includes('company') && list.filterForm.company_id) c += 1
    if (!list.readOnlyControl.includes('property') && list.filterForm.property_id) c += 1
    return c
  }, [list.filterForm, list.readOnlyControl])

  return (
    <div
      className="min-h-screen bg-[#f8fafc] pb-12 font-sans text-slate-900"
      data-testid="yardi-invite-list-page"
      style={{ padding: 0 }}
    >
      <PageHeader title={pageTitle} testIdRoot="yardi-invite-list-page-header" />

      <ConfirmShellModal
        isOpen={list.retryErrorModal != null}
        title={list.retryErrorModal?.title ?? 'Error'}
        bodyHtml={list.retryErrorModal?.bodyHtml ?? ''}
        onClose={list.clearRetryErrorModal}
        overlayDataTestId="yardi-invite-retry-error-modal-overlay"
        panelDataTestId="yardi-invite-retry-error-modal-panel"
        closeButtonDataTestId="yardi-invite-retry-error-modal-close"
        okButtonDataTestId="yardi-invite-retry-error-modal-ok"
      />

      <div
        className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8"
        data-testid="yardi-invite-list-main"
      >
        <div
          className="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between"
          data-testid="yardi-invite-list-toolbar"
        >
          <div className="relative w-full min-w-0 max-w-lg md:w-1/2" data-testid="yardi-invite-list-search-wrap">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </div>
            <input
              type="text"
              data-testid="yardi-invite-list-search-input"
              placeholder="Search by prospect ID or name..."
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-12 pr-4 text-sm shadow-sm transition-all placeholder:text-slate-400 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15"
              value={list.filterForm.q}
              onChange={(e) => {
                list.patchFilterForm({ q: e.target.value })
                scheduleMainSearchSubmit()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (mainSearchDebounceRef.current) {
                    clearTimeout(mainSearchDebounceRef.current)
                    mainSearchDebounceRef.current = null
                  }
                  list.filterSubmit()
                }
              }}
            />
          </div>

          <div
            className="flex w-full flex-wrap items-center gap-2 sm:gap-3 md:w-auto md:flex-nowrap"
            data-testid="yardi-invite-list-toolbar-actions"
          >
            <button
              type="button"
              data-testid="yardi-invite-list-filters-button"
              onClick={() => setIsFilterOpen(true)}
              className={`relative flex flex-1 items-center justify-center gap-2 rounded-xl border bg-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-all hover:bg-slate-50 hover:shadow-md md:flex-none whitespace-nowrap ${
                activeFilterCount > 0 ? 'border-[rgb(21_151_255)] text-[rgb(21_151_255)]' : 'border-slate-200 text-slate-700'
              }`}
            >
              <YardiToolbarFunnelIcon className="shrink-0 text-current" />
              Filters
              {activeFilterCount > 0 && (
                <span
                  data-testid="yardi-invite-list-filters-active-count"
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[rgb(21_151_255)] px-1 text-xs font-bold leading-none text-white"
                  aria-label={`${activeFilterCount} filters applied`}
                >
                  {activeFilterCount > 99 ? '99+' : activeFilterCount}
                </span>
              )}
            </button>
            <Link
              to="/yardiinvite/search"
              data-testid="yardi-invite-search-guest-link"
              className="flex min-w-[11rem] flex-[2] items-center justify-center gap-2 rounded-xl bg-[rgb(21_151_255)] px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all hover:-translate-y-0.5 hover:bg-[#0d7acc] hover:shadow-xl md:flex-none whitespace-nowrap"
            >
              <Search className="h-5 w-5" aria-hidden />
              Search / Invite Guest Card
            </Link>
          </div>
        </div>

        <YardiInviteFiltersDrawer
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          list={list}
        />

        <div
          data-testid="yardi-invite-list-results-card"
          className="mt-4 flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          {list.processing ? (
            <div
              data-testid="yardi-invite-list-loading"
              className="flex min-h-[280px] flex-1 items-center justify-center py-20"
            >
              <Loader2 className="h-8 w-8 animate-spin text-[rgb(21_151_255)]" aria-label="Loading" />
            </div>
          ) : (
            <>
          <div
            data-testid="yardi-invite-list-pagination-header-bar"
            className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-4"
          >
            <div
              data-testid="yardi-invite-list-pagination-header-summary"
              className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2 text-center text-xs font-medium text-slate-500 sm:justify-start sm:text-left sm:text-sm"
            >
              <span>Page</span>
              {list.pageOptions.last > 1 ? (
                <input
                  type="number"
                  min={1}
                  max={list.pageOptions.last}
                  data-testid="yardi-invite-list-goto-page-input-header"
                  className="mx-1 w-14 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-center text-sm font-bold text-slate-900 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 sm:w-16"
                  value={gotoPageValue}
                  onChange={(e) => setGotoPageInput(e.target.value)}
                  onBlur={commitGotoPage}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      commitGotoPage()
                    }
                  }}
                  aria-label="Go to page"
                />
              ) : (
                <span className="mx-1 font-bold text-slate-900">{list.pageOptions.current || 1}</span>
              )}
              <span>of</span>
              <span className="font-bold text-slate-900">{list.pageOptions.last || 1}</span>
              {list.pageOptions.total > 0 && (
                <>
                  <span className="mx-2 hidden text-slate-300 sm:inline">|</span>
                  <span>Total</span>
                  <span className="font-bold text-slate-900">{list.pageOptions.total}</span>
                  <span>Records</span>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <YardiPagination
                pageOptions={list.pageOptions}
                onPageChange={list.paginateItem}
                placement="top"
              />
            </div>
          </div>

        <div className="min-w-0 overflow-x-hidden" data-testid="yardi-invite-list-data-scroll">
          <div className="min-w-full" data-testid="yardi-invite-list-data-container">
        {isListView ? (
            <div className={yardiTableStyles.tableCard} data-testid="yardi-invite-list-table-card">
              <div
                className={`${yardiTableStyles.tableResponsiveScroll} touch-scroll`}
                data-testid="yardi-invite-list-table-scroll"
              >
                <table
                  className={cn(
                    yardiTableStyles.listingTable,
                    yardiTableStyles.yardiListingTable,
                    yardiTableStyles.yardiInviteListTable,
                    'table-fixed w-full min-w-[1080px] max-w-none [&>thead>tr>th]:!whitespace-normal [&>tbody>tr>td:not([colspan])]:!whitespace-normal',
                    '[&>thead>tr>th:not(:last-child)]:break-words [&>tbody>tr>td:not([colspan])]:break-words',
                    '[&>tbody>tr>td:not([colspan])]:!px-1.5 [&>tbody>tr>td:not([colspan])]:!py-2 [&>tbody>tr>td:not([colspan])]:!text-[12px] [&>tbody>tr>td:not([colspan])]:!font-normal [&>tbody>tr>td:not([colspan])]:!leading-snug',
                  )}
                  data-testid="yardi-invite-list-table"
                >
                  <colgroup>
                    {YARDI_INVITE_COL_WIDTHS_PCT.map((pct, i) => (
                      <col key={i} style={{ width: `${pct}%` }} />
                    ))}
                  </colgroup>
                  <thead data-testid="yardi-invite-list-table-head">
                    <tr>
                      <th data-testid="yardi-invite-list-table-col-expand" aria-label="Expand" />
                      {(
                        [
                          ['yardi_application_id', 'ID'],
                          ['company_name', 'Company Name'],
                          ['property_name', 'Property Name'],
                          ['property_code', 'Property Code'],
                          ['agent_name', 'Agent Name'],
                          ['unit_number', 'Unit Number'],
                          ['lease_start_date', 'Lease Start Date'],
                          ['lr_check_error', 'Application Failure Reason'],
                          ['state', 'Status'],
                          ['manual', 'Manual'],
                        ] as const
                      ).map(([col, label]) => (
                        <th
                          key={col}
                          data-testid={`yardi-invite-list-table-sort-${col}`}
                          className={cn(
                            'cursor-pointer select-none hover:bg-slate-100/80',
                            list.orderBy === col && '!text-[rgb(21_151_255)]',
                          )}
                          onClick={() => list.sortColumn(col)}
                        >
                          <SortHeader column={col}>{label}</SortHeader>
                        </th>
                      ))}
                      <th data-testid="yardi-invite-list-table-col-actions">Action</th>
                    </tr>
                  </thead>
                  <tbody data-testid="yardi-invite-list-table-body">
                    {!list.invites || list.invites.length === 0 ? (
                      <TableSearchFilterEmptyTableRow
                        colSpan={12}
                        rowDataTestId="yardi-invite-list-table-empty-row"
                        data-testid="yardi-invite-list-table-empty-message"
                      />
                    ) : (
                      list.invites.map((invite, idx) => {
                        const isLast = idx === list.invites!.length - 1
                        const rowSuffix = sanitizeDataTestIdSegment(invite.yardi_application_id)
                        return (
                          <Fragment key={invite.yardi_application_id}>
                            <tr data-testid={`yardi-invite-list-table-row-${rowSuffix}`}>
                              <td data-testid={`yardi-invite-list-table-row-expand-${rowSuffix}`}>
                                <button
                                  type="button"
                                  data-testid={`yardi-invite-list-expand-applicants-button-${rowSuffix}`}
                                  className={cn(
                                    'inline-flex items-center justify-center rounded border-0 bg-transparent p-1 text-slate-700',
                                    'hover:bg-slate-100/90 hover:text-slate-900',
                                    'focus-visible:outline focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)]/40 focus-visible:ring-offset-1',
                                  )}
                                  onClick={() => toggleApplicant(invite.yardi_application_id)}
                                  aria-label="Toggle applicants"
                                >
                                  {selectedApplicationId === invite.yardi_application_id ? (
                                    <ChevronUp className="h-4 w-4" aria-hidden />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" aria-hidden />
                                  )}
                                </button>
                              </td>
                              <td data-testid={`yardi-invite-list-cell-yardi-application-id-${rowSuffix}`}>
                                {invite.yardi_application_id}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-company-name-${rowSuffix}`}>
                                {invite.company_name}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-property-name-${rowSuffix}`}>
                                <YardiPropertyNameLink
                                  propertyId={invite.property_id}
                                  propertyName={invite.property_name}
                                  dataTestId={`yardi-invite-list-property-name-link-${rowSuffix}`}
                                />
                              </td>
                              <td data-testid={`yardi-invite-list-cell-property-code-${rowSuffix}`}>
                                {invite.property_code}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-agent-name-${rowSuffix}`}>
                                {invite.agent_name}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-unit-number-${rowSuffix}`}>
                                {invite.unit_number}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-lease-start-date-${rowSuffix}`}>
                                {formatShortDate(invite.lease_start_date)}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-lr-check-error-${rowSuffix}`}>
                                <LrErrorCell
                                  invite={invite}
                                  isLastRow={isLast}
                                  rowSuffix={rowSuffix}
                                />
                              </td>
                              <td data-testid={`yardi-invite-list-cell-status-${rowSuffix}`}>
                                {YARDI_STATUS_MAPPING[invite.state ?? ''] || invite.state}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-manual-${rowSuffix}`}>
                                {manualLabel(invite)}
                              </td>
                              <td data-testid={`yardi-invite-list-cell-action-${rowSuffix}`}>
                                {invite.state === 'FAILED' && (
                                  <div className={yardiTableStyles.actionButtons}>
                                    <button
                                      type="button"
                                      data-testid={`yardi-invite-list-retry-${rowSuffix}`}
                                      disabled={list.retryingApplicationId != null}
                                      className={cn(
                                        'relative inline-flex items-center justify-center rounded-lg border-0 bg-[rgb(21_151_255)] px-3 py-1.5 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all md:flex-none',
                                        'hover:bg-[#0d7acc] hover:shadow-xl',
                                        'disabled:pointer-events-none disabled:opacity-50',
                                      )}
                                      onClick={() => list.retryapplication(invite.yardi_application_id)}
                                    >
                                      <span
                                        className={
                                          list.retryingApplicationId === invite.yardi_application_id
                                            ? 'invisible'
                                            : undefined
                                        }
                                        aria-hidden={list.retryingApplicationId === invite.yardi_application_id}
                                      >
                                        Retry
                                      </span>
                                      {list.retryingApplicationId === invite.yardi_application_id ? (
                                        <Loader2
                                          className="absolute h-4 w-4 animate-spin"
                                          aria-label="Retrying"
                                        />
                                      ) : null}
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                            {selectedApplicationId === invite.yardi_application_id && (
                              <tr data-testid={`yardi-invite-list-table-row-detail-${rowSuffix}`}>
                                <td
                                  colSpan={12}
                                  className="!bg-white !align-top !text-left"
                                  data-testid={`yardi-invite-list-table-applicant-detail-cell-${rowSuffix}`}
                                >
                                  <YardiApplicantDetailPanel applicationId={invite.yardi_application_id} />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
        ) : (
          <div
            className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 xl:grid-cols-3"
            data-testid="yardi-invite-list-mobile-cards"
          >
            {list.invites && list.invites.length > 0 ? (
              list.invites.map((invite, idx) => {
                const isLastCard = idx === list.invites!.length - 1
                const rowSuffix = sanitizeDataTestIdSegment(invite.yardi_application_id)
                return (
                  <div
                    key={invite.yardi_application_id}
                    data-testid={`yardi-invite-list-mobile-card-${rowSuffix}`}
                    className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-3">
                      <div
                        className="min-w-0 flex-1"
                        data-testid={`yardi-invite-list-mobile-field-yardi-application-id-${rowSuffix}`}
                      >
                        <p className="text-xs font-semibold text-slate-500">Yardi Application ID</p>
                        <p className="break-words text-base font-semibold text-slate-900">{invite.yardi_application_id}</p>
                      </div>
                      <button
                        type="button"
                        className={cn(
                          'shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700',
                          'hover:bg-slate-100',
                        )}
                        data-testid={`yardi-invite-list-mobile-expand-${rowSuffix}`}
                        onClick={() => toggleApplicant(invite.yardi_application_id)}
                        aria-expanded={selectedApplicationId === invite.yardi_application_id}
                        aria-label="Toggle applicants"
                      >
                        <span className="inline-flex items-center gap-1">
                          {selectedApplicationId === invite.yardi_application_id ? (
                            <>
                              Hide
                              <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                            </>
                          ) : (
                            <>
                              Applicants
                              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                            </>
                          )}
                        </span>
                      </button>
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-company-name-${rowSuffix}`}>
                      <p className="mt-2 text-xs font-semibold text-slate-500">Company Name</p>
                      <p className="break-words text-sm text-slate-900">{invite.company_name}</p>
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-property-name-${rowSuffix}`}>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Property Name</p>
                      <p className="break-words text-sm text-slate-900">
                        <YardiPropertyNameLink
                          propertyId={invite.property_id}
                          propertyName={invite.property_name}
                          dataTestId={`yardi-invite-list-mobile-property-name-link-${rowSuffix}`}
                        />
                      </p>
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-property-code-${rowSuffix}`}>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Property Code</p>
                      <p className="break-words text-sm text-slate-900">{invite.property_code}</p>
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-agent-name-${rowSuffix}`}>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Agent Name</p>
                      <p className="break-words text-sm text-slate-900">{invite.agent_name}</p>
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-unit-number-${rowSuffix}`}>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Unit Number</p>
                      <p className="text-sm text-slate-900">{invite.unit_number}</p>
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-lease-start-date-${rowSuffix}`}>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Lease Start Date</p>
                      <p className="text-sm text-slate-900">{formatShortDate(invite.lease_start_date)}</p>
                    </div>
                    <p
                      className="mt-1.5 text-xs font-semibold text-slate-500"
                      data-testid={`yardi-invite-list-mobile-lr-error-label-${rowSuffix}`}
                    >
                      Application Failure Reason
                    </p>
                    <div
                      className="text-sm text-slate-900"
                      data-testid={`yardi-invite-list-mobile-field-lr-check-error-${rowSuffix}`}
                    >
                      <LrErrorCell
                        invite={invite}
                        isLastRow={isLastCard}
                        rowSuffix={rowSuffix}
                      />
                      {!invite.lr_check_error && (
                        <span className="text-slate-400">—</span>
                      )}
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-status-${rowSuffix}`}>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Status</p>
                      <p className="text-sm text-slate-900">
                        {YARDI_STATUS_MAPPING[invite.state ?? ''] || invite.state}
                      </p>
                    </div>
                    <div data-testid={`yardi-invite-list-mobile-field-manual-${rowSuffix}`}>
                      <p className="mt-1.5 text-xs font-semibold text-slate-500">Manual</p>
                      <p className="text-sm text-slate-900">{manualLabel(invite)}</p>
                    </div>
                    {invite.state === 'FAILED' && (
                      <button
                        type="button"
                        disabled={list.retryingApplicationId != null}
                        data-testid={`yardi-invite-list-mobile-retry-${rowSuffix}`}
                        className={cn(
                          'relative mt-2 inline-flex w-full items-center justify-center rounded-lg border-0 bg-[rgb(21_151_255)] py-2 text-sm font-semibold text-white shadow-lg shadow-[rgba(21,151,255,0.25)] transition-all',
                          'hover:bg-[#0d7acc] hover:shadow-xl',
                          'disabled:pointer-events-none disabled:opacity-50',
                        )}
                        onClick={() => list.retryapplication(invite.yardi_application_id)}
                      >
                        <span
                          className={
                            list.retryingApplicationId === invite.yardi_application_id
                              ? 'invisible'
                              : undefined
                          }
                          aria-hidden={list.retryingApplicationId === invite.yardi_application_id}
                        >
                          Retry
                        </span>
                        {list.retryingApplicationId === invite.yardi_application_id ? (
                          <Loader2 className="absolute h-4 w-4 animate-spin" aria-label="Retrying" />
                        ) : null}
                      </button>
                    )}
                    {selectedApplicationId === invite.yardi_application_id && (
                      <div
                        className="mt-2 rounded-lg border border-slate-200 bg-white p-3"
                        data-testid={`yardi-invite-list-mobile-detail-${rowSuffix}`}
                      >
                        <YardiApplicantDetailPanel applicationId={invite.yardi_application_id} />
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <TableSearchFilterEmptyGridSlot data-testid="yardi-invite-list-mobile-empty" />
            )}
          </div>
        )}
          </div>
        </div>

          <div
            data-testid="yardi-invite-list-pagination-footer-bar"
            className="flex flex-col items-stretch justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:gap-4 sm:px-6 sm:py-4"
          >
            <div
              data-testid="yardi-invite-list-pagination-footer-summary"
              className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2 text-center text-xs font-medium text-slate-500 sm:justify-start sm:text-left sm:text-sm"
            >
              <span>Page</span>
              {list.pageOptions.last > 1 ? (
                <input
                  type="number"
                  min={1}
                  max={list.pageOptions.last}
                  data-testid="yardi-invite-list-goto-page-input-footer"
                  className="mx-1 w-14 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-center text-sm font-bold text-slate-900 focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 sm:w-16"
                  value={gotoPageValue}
                  onChange={(e) => setGotoPageInput(e.target.value)}
                  onBlur={commitGotoPage}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      commitGotoPage()
                    }
                  }}
                  aria-label="Go to page"
                />
              ) : (
                <span className="mx-1 font-bold text-slate-900">{list.pageOptions.current || 1}</span>
              )}
              <span>of</span>
              <span className="font-bold text-slate-900">{list.pageOptions.last || 1}</span>
              {list.pageOptions.total > 0 && (
                <>
                  <span className="mx-2 hidden text-slate-300 sm:inline">|</span>
                  <span>Total</span>
                  <span className="font-bold text-slate-900">{list.pageOptions.total}</span>
                  <span>Records</span>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <YardiPagination
                pageOptions={list.pageOptions}
                onPageChange={list.paginateItem}
                placement="bottom"
              />
              <div className="ml-0 flex items-center gap-2 sm:ml-4">
                <span
                  className="text-xs font-semibold tracking-wider text-slate-400 uppercase"
                  data-testid="yardi-invite-per-page-label-footer"
                >
                  Rows
                </span>
                <select
                  className="block rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/25"
                  data-testid="yardi-invite-per-page-select-footer"
                  value={list.perPage}
                  onChange={(e) => list.changePerPage(Number(e.target.value))}
                  aria-label="Rows per page"
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
